/**
 * Sound effects using Web Audio API
 * No external dependencies required
 */

import { loadPersistedState } from '../store/persist';

// Audio context singleton
let audioContext: AudioContext | null = null;

/**
 * Initialize audio context on first user interaction
 */
export function initAudio(): void {
  if (audioContext) return;
  
  try {
    audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  } catch (e) {
    console.warn('Web Audio API not supported');
  }
}

/**
 * Check if sound is enabled in settings
 */
function isSoundEnabled(): boolean {
  const persisted = loadPersistedState();
  return persisted.settings?.soundEnabled ?? true;
}

/**
 * Play a card flip sound (short swish)
 */
export function playCardFlip(): void {
  if (!isSoundEnabled() || !audioContext) return;
  
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  // Swish sound: frequency sweep
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.1);
  
  gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.1);
}

/**
 * Play correct answer sound (pleasant chime)
 */
export function playCorrect(): void {
  if (!isSoundEnabled() || !audioContext) return;
  
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  // Two-tone chime
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
  oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
  
  gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
  gainNode.gain.setValueAtTime(0.15, audioContext.currentTime + 0.1);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.4);
}

/**
 * Play wrong answer sound (low buzz)
 */
export function playWrong(): void {
  if (!isSoundEnabled() || !audioContext) return;
  
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  // Low buzz
  oscillator.type = 'sawtooth';
  oscillator.frequency.setValueAtTime(150, audioContext.currentTime);
  oscillator.frequency.linearRampToValueAtTime(100, audioContext.currentTime + 0.3);
  
  gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.3);
}

/**
 * Play game win sound (triumphant chord)
 */
export function playWin(): void {
  if (!isSoundEnabled() || !audioContext) return;
  
  const frequencies = [523.25, 659.25, 783.99, 1046.50]; // C major chord
  
  frequencies.forEach((freq, i) => {
    const oscillator = audioContext!.createOscillator();
    const gainNode = audioContext!.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext!.destination);
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, audioContext!.currentTime + i * 0.05);
    
    gainNode.gain.setValueAtTime(0, audioContext!.currentTime + i * 0.05);
    gainNode.gain.linearRampToValueAtTime(0.1, audioContext!.currentTime + i * 0.05 + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext!.currentTime + 1);
    
    oscillator.start(audioContext!.currentTime + i * 0.05);
    oscillator.stop(audioContext!.currentTime + 1);
  });
}

/**
 * Play game lose sound (sad descending)
 */
export function playLose(): void {
  if (!isSoundEnabled() || !audioContext) return;
  
  const frequencies = [392.00, 349.23, 311.13, 293.66]; // Descending minor
  
  frequencies.forEach((freq, i) => {
    const oscillator = audioContext!.createOscillator();
    const gainNode = audioContext!.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext!.destination);
    
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(freq, audioContext!.currentTime + i * 0.15);
    
    gainNode.gain.setValueAtTime(0.1, audioContext!.currentTime + i * 0.15);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext!.currentTime + i * 0.15 + 0.3);
    
    oscillator.start(audioContext!.currentTime + i * 0.15);
    oscillator.stop(audioContext!.currentTime + i * 0.15 + 0.3);
  });
}

/**
 * Play hint used sound
 */
export function playHint(): void {
  if (!isSoundEnabled() || !audioContext) return;
  
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  // Magic sparkle sound
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.15);
  
  gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.15);
}
